/**
 * Provider-agnostic analytics service.
 *
 * Swap the provider implementation when ready to integrate
 * a real analytics service (PostHog, Mixpanel, etc.).
 *
 * Events are logged to console in development mode.
 */

type AnalyticsEvent = {
  name: string;
  properties?: Record<string, unknown>;
};

type UserTraits = {
  id: string;
  email?: string;
  role?: string;
  organization?: string;
  plan?: string;
};

class Analytics {
  private initialized = false;
  private userId: string | null = null;

  init() {
    // Replace with real provider init:
    // posthog.init('phc_...', { api_host: 'https://app.posthog.com' });
    this.initialized = true;
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Initialized');
    }
  }

  identify(traits: UserTraits) {
    this.userId = traits.id;
    // Replace with: posthog.identify(traits.id, traits);
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Identify:', traits);
    }
  }

  track(event: string, properties?: Record<string, unknown>) {
    const payload: AnalyticsEvent = { name: event, properties };
    // Replace with: posthog.capture(event, properties);
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Track:', payload);
    }
  }

  page(name?: string, properties?: Record<string, unknown>) {
    // Replace with: posthog.capture('$pageview', { ...properties, pageName: name });
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Page:', name, properties);
    }
  }

  reset() {
    this.userId = null;
    // Replace with: posthog.reset();
    if (import.meta.env.DEV) {
      console.debug('[Analytics] Reset');
    }
  }
}

export const analytics = new Analytics();

// Pre-defined event names for consistency
export const EVENTS = {
  // Auth
  LOGIN: 'user_login',
  LOGOUT: 'user_logout',
  SIGNUP: 'user_signup',

  // Onboarding
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',

  // Core features
  CORRESPONDENCE_CREATED: 'correspondence_created',
  CORRESPONDENCE_VIEWED: 'correspondence_viewed',
  RESPONSE_CREATED: 'response_created',
  RESPONSE_SUBMITTED: 'response_submitted',
  PROJECT_CREATED: 'project_created',
  EMAIL_SYNCED: 'email_synced',
  EMAIL_ACCOUNT_CONNECTED: 'email_account_connected',
  DOCUMENT_UPLOADED: 'document_uploaded',

  // Workflow
  WORKFLOW_CREATED: 'workflow_created',
  WORKFLOW_STEP_COMPLETED: 'workflow_step_completed',

  // Search
  SEARCH_PERFORMED: 'search_performed',

  // AI
  AI_CLASSIFICATION_USED: 'ai_classification_used',
  AI_RESPONSE_GENERATED: 'ai_response_generated',
} as const;
